import classnames from "classnames";
import { useForm } from "react-hook-form";
import { useContext, useEffect, useState } from "react";
import quoteService from "services/quote.service";
import { yupResolver } from "@hookform/resolvers/yup";
import { bidFormValidationSchema } from "./bidFormValidationSchema";
import { fetchBidsData, getRfqData, getTruck } from "pages/Dashboard/utils";
import Checkbox from "components/Checkbox";
import Dropdown from "components/Dropdown";
import Button, { STYLE as BTN_STYLE, COLOR } from "components/Button";
import FormField, { STYLE, TYPE } from "components/FormField";
import SearchSelection from "../SearchSelection";
import { Context } from "pages/Dashboard/Dashboard";

import styles from "./BidForm.module.scss";

const truckTypeList = [
  { title: "Cargo Van", value: "cargoVan" },
  { title: "Sprinter Van", value: "sprinterVan" },
  { title: "Small Straight", value: "smallStraight" },
  { title: "Large Straight", value: "largeStraight" },
  { title: "Reefer", value: "reefer" },
  { title: "Truckload Dry Van", value: "truckloadDryVan" },
];

const BidForm = ({
  setShowModal,
  availableData,
  setErrorModal,
  setErrorMessage,
  setSuccess,
  setLoading,
  setPercentage,
  setDisabled,
  setAccepted,
  setEmptyState,
  state,
}) => {
  const { setBidsData, setRfqData } = useContext(Context);
  const [checked, setChecked] = useState(false);
  const [truckType, setTruckType] = useState({
    name: {
      title: "",
      value: "",
    },
  });
  const [unit, setUnit] = useState({
    name: {
      title: "",
      value: "",
    },
  });

  const { register, handleSubmit, formState, setValue, clearErrors } = useForm({
    defaultValues: {
      email: availableData.email,
      customerRate: "",
      driverRate: "",
      unit: "",
      truckType: "",
      milesOut: "",
      eta: "",
      transitTime: "",
      length: "",
      width: "",
      height: "",
      notes: "",
    },
    resolver: yupResolver(bidFormValidationSchema),
  });

  const { errors } = formState;

  const handleChange = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    setValue("blindBid", checked);
    clearErrors([
      "unit",
      "truckType",
      "length",
      "width",
      "height",
      "milesOut",
      "eta",
      "transitTime",
    ]);
  }, [setValue, checked, clearErrors]);

  useEffect(() => {
    setValue("truckType", truckType.name.title);
  }, [setValue, truckType]);

  useEffect(() => {
    if (availableData.item) {
      setUnit({
        name: { title: availableData.item.unit, value: availableData.item.unit },
      });

      getTruck({ title: availableData.item.unit, setValue, setTruckType, state });
    }
  }, [availableData.item, availableData, setValue]);

  const onSubmit = (data) => {
    const bid = {
      RfqId: availableData.id,
      AgentEmail: data.email,
      CustomerRate: data.customerRate,
      DriverRate: data.driverRate,
      UnitNumber: data.unit,
      TruckType: data.truckType,
      Dimensions: {
        LengthIn: data.length,
        WidthIn: data.width,
        HeightIn: data.height,
      },
      MilesOut: data.milesOut,
      ETA: data.eta,
      TransitTimeOnceLoaded: data.transitTime,
      Notes: data.notes,
      BlindBid: checked,
    };

    setDisabled(true);

    quoteService.postBid(bid).then(
      ({ status }) => {
        if (status === 200) {
          setShowModal(false);
          setSuccess(true);
          getRfqData({ setLoading, setPercentage, setRfqData, setEmptyState, state });
          setDisabled(false);
          setAccepted(false);
          fetchBidsData({ setBidsData });
        }
      },
      ({ response }) => {
        const { status, data } = response;
        setDisabled(false);
        if (status !== 200) {
          setShowModal(false);
          setErrorModal((prev) => ({ ...prev, state: true }));
          setErrorMessage(data.error);
        } else {
          console.error(response);
        }
      },
    );
  };

  return (
    <>
      <h2 className={styles.title}>MAKE BID FORM</h2>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.group}>
          <FormField
            labelText="Agent Email *"
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="email"
            register={register}
            error={errors.email}
          />
          <Checkbox
            id="blindBid"
            label="Blind Bid"
            value={checked}
            onChange={handleChange}
            className={styles.checkbox}
            error={errors.blindBid}
          />
        </div>
        <div className={styles.group}>
          <FormField
            labelText="Customer Rate *"
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="customerRate"
            register={register}
            error={errors.customerRate}
            placeholderText="2549.50"
          />
          <FormField
            labelText={`Driver’s Rate ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="driverRate"
            register={register}
            error={errors.driverRate}
            placeholderText="2000"
          />
        </div>
        <div className={styles.group}>
          <SearchSelection
            unit={unit}
            setUnit={setUnit}
            setValue={setValue}
            checked={checked}
            className={classnames(styles.formField, styles.thinField, styles.dropdown)}
            error={errors.unit}
            placeholderText="1234"
            setTruckType={setTruckType}
            state={state}
          />
          <Dropdown
            list={truckTypeList}
            label={`Truck Type ${checked ? "" : "*"}`}
            withInput={true}
            keyValue="name"
            value={truckType}
            setValue={setTruckType}
            className={classnames(styles.formField, styles.dropdown, styles.withIcon)}
            error={errors.truckType}
          />
        </div>
        <div className={styles.group}>
          <FormField
            labelText={`Length, in ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="length"
            register={register}
            error={errors.length}
          />
          <FormField
            labelText={`Width, in ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="width"
            register={register}
            error={errors.width}
          />
          <FormField
            labelText={`Height, in ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="height"
            register={register}
            error={errors.height}
          />
        </div>
        <div className={styles.group}>
          <FormField
            labelText={`Miles Out ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="milesOut"
            register={register}
            error={errors.milesOut}
            placeholderText="150"
          />
          <FormField
            labelText={`ETA to PU ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="eta"
            register={register}
            error={errors.eta}
            placeholderText="2h"
          />
          <FormField
            labelText={`Transit Time ${checked ? "" : "*"}`}
            style={STYLE.LIGHT}
            className={styles.formField}
            fieldName="transitTime"
            register={register}
            error={errors.transitTime}
            placeholderText="18h"
          />
        </div>
        <FormField
          labelText="Notes"
          type={TYPE.TEXTAREA}
          style={STYLE.LIGHT}
          className={styles.formField}
          fieldName="notes"
          register={register}
          error={errors.notes}
        />
        <div className={styles.buttonsGroup}>
          <Button
            children="BACK"
            style={COLOR.NONE}
            className={classnames(styles.button, styles.decline)}
            onClick={() => setShowModal(false)}
          />
          <Button type="submit" children="BID" className={styles.button} />
        </div>
      </form>
    </>
  );
};

export default BidForm;
